'use client';

import { TrashIcon } from '@heroicons/react/24/outline';
import React from 'react';

const SOCIAL_OPTIONS = [
    {
        key: 'email',
        label: 'Email',
        icon: 'fa-solid fa-envelope',
        aliases: ['mail'],
    },
    {
        key: 'phone',
        label: 'Phone',
        icon: 'fa-solid fa-phone',
        aliases: ['tel'],
    },
    { key: 'linkedin', label: 'LinkedIn', icon: 'fa-brands fa-linkedin-in' },
    { key: 'facebook', label: 'Facebook', icon: 'fa-brands fa-facebook' },
    { key: 'instagram', label: 'Instagram', icon: 'fa-brands fa-instagram' },
    {
        key: 'x',
        label: 'X',
        icon: 'fa-brands fa-x-twitter',
        aliases: ['twitter'],
    },
    { key: 'google', label: 'Google', icon: 'fa-brands fa-google' },
    { key: 'youtube', label: 'YouTube', icon: 'fa-brands fa-youtube' },
    { key: 'pinterest', label: 'Pinterest', icon: 'fa-brands fa-pinterest' },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'fa-brands fa-whatsapp' },
    { key: 'sms', label: 'SMS', icon: 'fa-solid fa-message-sms' },
    { key: 'tiktok', label: 'TikTok', icon: 'fa-brands fa-tiktok' },
    { key: 'yelp', label: 'Yelp', icon: 'fa-brands fa-yelp' },
    { key: 'houzz', label: 'Houzz', icon: 'fa-brands fa-houzz' },
    { key: 'mobilo', label: 'Mobilo', icon: 'fa-kit fa-mobilo' },
    { key: 'flickr', label: 'Flickr', icon: 'fa-brands fa-flickr' },
    {
        key: 'angi',
        label: 'Angi',
        icon: 'fa-kit fa-angi',
        aliases: ["angi's list", "angie's list", 'angislist', 'angieslist'],
    },
    {
        key: 'bbb',
        label: 'BBB',
        icon: 'fa-kit fa-bbb',
        aliases: ['better business bureau', 'betterbusinessbureau'],
    },
    { key: 'capterra', label: 'Capterra', icon: 'fa-kit fa-capterra' },
    { key: 'homestars', label: 'HomeStars', icon: 'fa-kit fa-homestars' },
    { key: 'nextdoor', label: 'Nextdoor', icon: 'fa-kit fa-nextdoor' },
    { key: 'thumbtack', label: 'Thumbtack', icon: 'fa-kit fa-thumbtack' },
    {
        key: 'yellowpages',
        label: 'YP',
        icon: 'fa-kit fa-yellowpages',
        aliases: ['yellow pages', 'yellowpages', 'yp'],
    },
    { key: 'nicejob', label: 'NiceJob', icon: 'fa-kit fa-nicejob' },
];

const SOCIAL_LINK_KEYS = ['Link', 'URL', 'Url', 'Href', 'link', 'href'];

function normalizeKey(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

function getSocialName(item) {
    if (!item || typeof item !== 'object') return '';
    return item.Name || item.Label || item.Title || item.name || '';
}

function getSocialLink(item) {
    if (!item || typeof item !== 'object') return '';
    for (const key of SOCIAL_LINK_KEYS) {
        if (item[key]) return item[key];
    }
    return '';
}

function inferKeyFromLink(link) {
    const s = String(link || '')
        .toLowerCase()
        .trim();
    if (!s) return '';
    if (s.startsWith('mailto:') || s.includes('@')) return 'email';
    if (s.startsWith('tel:')) return 'phone';
    if (s.startsWith('sms:')) return 'sms';
    if (/^\+?\d[\d\s().-]*$/.test(s)) return 'phone';
    return '';
}

function matchesOption(option, item) {
    const nameKey = normalizeKey(getSocialName(item));
    if (!nameKey && option.key === 'email') {
        const link = getSocialLink(item);
        if (typeof link === 'string' && /@/.test(link)) return true;
    }
    if (!nameKey) {
        const inferred = inferKeyFromLink(getSocialLink(item));
        if (inferred && inferred === option.key) return true;
        if (
            inferred &&
            Array.isArray(option.aliases) &&
            option.aliases.some((a) => normalizeKey(a) === inferred)
        )
            return true;
    }
    if (!nameKey) return false;
    const aliases = Array.isArray(option.aliases) ? option.aliases : [];
    return [option.key, ...aliases].some(
        (candidate) => normalizeKey(candidate) === nameKey
    );
}

export default function SocialLinksEditor({
    value,
    onChange,
    label = 'Social',
    className = '',
}) {
    const [dragKey, setDragKey] = React.useState(null);
    const [hoverKey, setHoverKey] = React.useState(null);
    const rawEntries = Array.isArray(value) ? value : [];

    const optionMap = React.useMemo(() => {
        const m = new Map();
        SOCIAL_OPTIONS.forEach((opt) => m.set(opt.key, opt));
        return m;
    }, []);

    // Deduplicate entries by social key to avoid duplicates during reorder
    const dedupeEntries = React.useCallback((list) => {
        const seen = new Set();
        const result = [];
        list.forEach((item) => {
            const opt = SOCIAL_OPTIONS.find((o) => matchesOption(o, item));
            const key =
                opt?.key || inferKeyFromLink(getSocialLink(item)) || null;
            if (key && !seen.has(key)) {
                seen.add(key);
                result.push({
                    ...item,
                    Name: item?.Name ?? opt?.label ?? key,
                });
            }
        });
        return result;
    }, []);

    const entries = React.useMemo(
        () => dedupeEntries(rawEntries),
        [rawEntries, dedupeEntries]
    );

    // Auto-sanitize incoming data to strip any pre-existing duplicates
    React.useEffect(() => {
        if (!onChange) return;
        if (rawEntries.length === 0) return;
        if (entries.length !== rawEntries.length) {
            onChange(entries);
        }
    }, [rawEntries, entries, onChange]);

    const findOptionForEntry = React.useCallback(
        (item) =>
            SOCIAL_OPTIONS.find((opt) => matchesOption(opt, item)) || null,
        []
    );

    const getIndexForOption = (option) =>
        entries.findIndex((item) => matchesOption(option, item));

    const handleAdd = (option) => {
        if (!onChange) return;
        const index = getIndexForOption(option);
        if (index !== -1) return;
        const next = [
            ...entries,
            {
                Name: option.label,
                Link: '',
            },
        ];
        onChange(dedupeEntries(next));
    };

    const handleRemove = (option) => {
        if (!onChange) return;
        const index = getIndexForOption(option);
        if (index === -1) return;
        const next = entries.slice();
        next.splice(index, 1);
        onChange(dedupeEntries(next));
    };

    const handleLinkChange = (option, nextValue) => {
        if (!onChange) return;
        const index = getIndexForOption(option);
        if (index === -1) return;
        const next = entries.slice();
        const current = next[index];
        next[index] = {
            ...current,
            Name: option.label,
            Link: nextValue,
        };
        onChange(dedupeEntries(next));
    };

    const commitReorder = () => {
        if (
            !dragKey ||
            !hoverKey ||
            dragKey === hoverKey ||
            !optionMap.has(dragKey) ||
            !optionMap.has(hoverKey)
        ) {
            setDragKey(null);
            setHoverKey(null);
            return;
        }
        const fromOpt = optionMap.get(dragKey);
        const toOpt = optionMap.get(hoverKey);
        const fromIdx = getIndexForOption(fromOpt);
        const toIdx = getIndexForOption(toOpt);
        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) {
            setDragKey(null);
            setHoverKey(null);
            return;
        }
        const next = entries.slice();
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        onChange(dedupeEntries(next));
        setDragKey(null);
        setHoverKey(null);
    };

    const onDragStart = (event, key) => {
        event.dataTransfer.setData('text/plain', key);
        event.dataTransfer.effectAllowed = 'move';
        setDragKey(key);
    };

    const onDragEnter = (event, key) => {
        event.preventDefault();
        if (dragKey && dragKey !== key) setHoverKey(key);
    };

    const onDragOver = (event, key) => {
        event.preventDefault();
        if (dragKey && dragKey !== key) setHoverKey(key);
    };

    const onDrop = (event) => {
        event.preventDefault();
        commitReorder();
    };

    const onDragEnd = () => {
        commitReorder();
    };

    const displayOptions = React.useMemo(() => {
        const selectedKeys = entries
            .map((item) => {
                const opt = SOCIAL_OPTIONS.find((o) => matchesOption(o, item));
                return opt ? opt.key : null;
            })
            .filter(Boolean);

        let previewKeys = selectedKeys;
        if (
            dragKey &&
            hoverKey &&
            dragKey !== hoverKey &&
            selectedKeys.includes(dragKey) &&
            selectedKeys.includes(hoverKey)
        ) {
            const withoutDrag = selectedKeys.filter((k) => k !== dragKey);
            const idx = withoutDrag.indexOf(hoverKey);
            if (idx !== -1) {
                withoutDrag.splice(idx, 0, dragKey);
                previewKeys = withoutDrag;
            }
        }

        const previewKeysSet = new Set(previewKeys);
        const previewSelected = previewKeys
            .map((k) => optionMap.get(k))
            .filter(Boolean);
        const remaining = SOCIAL_OPTIONS.filter(
            (opt) => !previewKeysSet.has(opt.key)
        );
        return [...previewSelected, ...remaining];
    }, [entries, dragKey, hoverKey, optionMap]);

    return (
        <div className={`mb-2 w-full ${className}`.trim()}>
            <div className="mb-2 text-xs font-semibold leading-5 text-primary-700">
                {label}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {displayOptions.map((option) => {
                    const index = getIndexForOption(option);
                    const isActive = index !== -1;
                    const draggable = isActive;
                    return (
                        <button
                            key={option.key}
                            type="button"
                            draggable={draggable}
                            aria-pressed={isActive}
                            aria-label={option.label}
                            title={option.label}
                            onClick={() => handleAdd(option)}
                            onDragStart={(e) => onDragStart(e, option.key)}
                            onDragEnter={(e) => onDragEnter(e, option.key)}
                            onDragOver={(e) => onDragOver(e, option.key)}
                            onDrop={onDrop}
                            onDragEnd={onDragEnd}
                            className={[
                                'inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-[13px] shadow-sm transition-all duration-75 ease-out',
                                isActive
                                    ? 'border-primary-600 text-primary-600'
                                    : 'border-neutral-300 text-neutral-500 hover:border-primary-400 hover:text-primary-600',
                                dragKey === option.key ? 'opacity-70' : '',
                            ].join(' ')}
                        >
                            <i className={option.icon} aria-hidden="true" />
                        </button>
                    );
                })}
            </div>

            {entries.map((entry, idx) => {
                const option = findOptionForEntry(entry);
                if (!option) return null;
                const linkValue = getSocialLink(entry);
                return (
                    <div key={`${option.key}-link-${idx}`} className="mt-3">
                        <label className="block text-xs font-semibold leading-5 text-primary-700">
                            {option.label} Link
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                            <div className="flex-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <input
                                    type="text"
                                    value={linkValue ?? ''}
                                    onChange={(event) =>
                                        handleLinkChange(
                                            option,
                                            event.target.value
                                        )
                                    }
                                    className="block h-9 w-full rounded-[10px] border-0 px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0 md:text-sm"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemove(option)}
                                className="inline-flex h-9 items-center justify-center rounded-lg border border-neutral-300 px-2 text-neutral-500 transition hover:border-red-300 hover:text-red-500"
                                aria-label={`Remove ${option.label}`}
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
